import { ReactGrid, Column } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import { useRef, memo, useCallback, useEffect, type FocusEvent } from 'react';
import useOutsideClick from '../../../../../hooks/useOutsideClick';
import { buildFocusedCellState } from '../../../utils/focusGridCell';
import { NewTextCellTemplate } from '../../../../../components/NewTextCellTemplate';
import { NewSelectCellTemplate } from '../../../../../components/NewSelectCellTemplate';
import { NewNumberCellTemplate } from '../../../../../components/NewNumberCellTemplate';
import { NewHeaderCellTemplate } from '../../../../../components/NewHeaderCellTemplate';
import { NewBooleanCellTemplate } from '../../../../../components/NewBooleanCellTemplate';
import { NewDateCellTemplate } from '../../../../../components/NewDateCellTemplate';
import { NewDateTimeCellTemplate } from '../../../../../components/NewDateTimeCellTemplate';

interface TableRow {
  rowId: number | string;
  height?: number;
  cells: any[];
}

interface TableProps {
  rows: TableRow[];
  columns: Column[];
  handleChanges: (cellChanges: any[]) => void;
  stickyLeftColumns?: number | null;
  stickyTopRows?: number | null;
  id?: string;
}

// How far the report may scroll before an open editor is closed. Under this the
// editor drifts from its cell by a few px, which reads as nothing; the trade is
// that nudging the page no longer costs you the cell you were working on.
const SCROLL_TOLERANCE_PX = 40;

const cellTemplates = {
  select: new NewSelectCellTemplate(),
  customText: new NewTextCellTemplate(),
  customNumber: new NewNumberCellTemplate(),
  customBoolean: new NewBooleanCellTemplate(),
  customDate: new NewDateCellTemplate(),
  customDateTime: new NewDateTimeCellTemplate(),
  customHeader: new NewHeaderCellTemplate()
};

export const Table = memo(
  ({ rows, columns, handleChanges, stickyLeftColumns = 2, stickyTopRows, id }: TableProps) => {
    const divRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<ReactGrid>(null);

    const handleOutsideClick = useCallback(() => {
      if (tableRef.current) {
        (tableRef.current as any).updateState(state => ({
          ...state,
          selectionMode: undefined,
          selectedRanges: [],
          focusedLocation: undefined
        }));
      }
    }, []);

    useOutsideClick(divRef, handleOutsideClick);

    // ReactGrid renders the open cell editor as `position: fixed`, so it only
    // lands correctly while the grid's scroll offsets are up to date. The report
    // scrolls on two different elements: vertically on the Virtuoso scroller and
    // horizontally on the `.scrolling-table` wrapper (the latter only overflows
    // in multi-fund view, from 4 funds up). ReactGrid tracks a single scroll
    // parent, so the editor ends up pinned to the viewport ("floats") instead of
    // following its cell. Close it on either axis; `focusedLocation` is left set
    // so the cell keeps its outline.
    useEffect(() => {
      const el = divRef.current;
      if (!el) return;

      const scrollers = [
        el.closest('[data-virtuoso-scroller]'),
        el.closest('.scrolling-table')
      ].filter(Boolean) as HTMLElement[];
      if (!scrollers.length) return;

      // Where the report stood when this cell was picked, so a small nudge can be
      // told apart from actually scrolling away. Captured on the first event
      // rather than on select, because nothing here observes selection.
      let anchor: number[][] | null = null;

      const onScroll = () => {
        const grid = tableRef.current as any;
        const state = grid?.state;
        // Mirrors the fields cleared below: once both are empty this bails out,
        // so a scroll gesture costs two property reads instead of re-rendering
        // every mounted grid on every frame.
        if (!state?.currentlyEditedCell && !state?.selectedRanges?.length) {
          anchor = null;
          return;
        }

        if (!anchor) {
          anchor = scrollers.map(s => [s.scrollTop, s.scrollLeft]);
          return;
        }

        const movedAway = scrollers.some(
          (s, i) =>
            Math.abs(s.scrollTop - anchor![i][0]) > SCROLL_TOLERANCE_PX ||
            Math.abs(s.scrollLeft - anchor![i][1]) > SCROLL_TOLERANCE_PX
        );
        if (!movedAway) return;
        anchor = null;

        // Blur first: the template's onBlur commits the typed value before the
        // editor unmounts, so scrolling away never discards an edit. Blur alone
        // would park focus on <body> and keydown would stop reaching the grid,
        // killing arrow-key navigation — so hand focus back to the element
        // ReactGrid itself focuses after a click. `preventScroll` keeps that
        // refocus from yanking the viewport mid-gesture.
        const active = document.activeElement as HTMLElement | null;
        if (active?.closest('.rg-celleditor')) {
          active.blur();
          state.hiddenFocusElement?.focus({ preventScroll: true });
        }

        grid.updateState((prev: any) => ({
          ...prev,
          selectedRanges: [],
          currentlyEditedCell: undefined
        }));
      };

      scrollers.forEach(s => s.addEventListener('scroll', onScroll, { passive: true }));
      return () => scrollers.forEach(s => s.removeEventListener('scroll', onScroll));
    }, []);

    // Clicking a native <select> cell doesn't reach ReactGrid (its pointerdown is
    // stopped so the dropdown can open), so the grid never focuses that cell and
    // arrow/Delete keys afterwards target the wrong one. When focus lands on such a
    // cell, sync the grid to it. Deferred so the re-render doesn't disturb the
    // opening native dropdown.
    const handleGridFocusIn = useCallback((e: FocusEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (!(target instanceof HTMLSelectElement)) return;
      const cellEl = target.closest('[data-cell-rowidx][data-cell-colidx]');
      if (!cellEl) return;
      const rowIdx = Number(cellEl.getAttribute('data-cell-rowidx'));
      const colIdx = Number(cellEl.getAttribute('data-cell-colidx'));
      if (Number.isNaN(rowIdx) || Number.isNaN(colIdx)) return;

      setTimeout(() => {
        const grid = tableRef.current as any;
        grid?.updateState((state: any) => buildFocusedCellState(state, rowIdx, colIdx));
      }, 0);
    }, []);

    return (
      <div ref={divRef} id={id} style={{ position: 'relative' }} onFocus={handleGridFocusIn}>
        <ReactGrid
          rows={rows}
          columns={columns}
          enableRangeSelection={false}
          enableFillHandle
          ref={tableRef}
          enableRowSelection={false}
          enableColumnSelection={false}
          horizontalStickyBreakpoint={90}
          verticalStickyBreakpoint={90}
          stickyLeftColumns={stickyLeftColumns ?? undefined}
          stickyTopRows={stickyTopRows ?? undefined}
          customCellTemplates={cellTemplates}
          onCellsChanged={handleChanges}
          disableVirtualScrolling={false}
        />
      </div>
    );
  },
  (prevProps: TableProps, nextProps: TableProps) => {
    return (
      prevProps.rows === nextProps.rows &&
      prevProps.columns === nextProps.columns &&
      prevProps.handleChanges === nextProps.handleChanges &&
      prevProps.stickyLeftColumns === nextProps.stickyLeftColumns &&
      prevProps.stickyTopRows === nextProps.stickyTopRows &&
      prevProps.id === nextProps.id
    );
  }
);

Table.displayName = 'Table';
